import type { Project } from "./types";

/**
 * Whether a monorepo's packages are one project or several.
 *
 * `package` is the default and what already ships: the logical key carries the
 * in-repo subpath, so `apps/web` and `apps/server` of one repository are
 * separate projects. That is right for a monorepo whose packages are different
 * work, and wrong for one you think of as a single thing.
 */
export type ProjectGroupingMode = "package" | "repository";

export const DEFAULT_PROJECT_GROUPING_MODE: ProjectGroupingMode = "package";

/** Grouping choices, keyed by the repository's canonical key. */
export type ProjectGroupingOverrides = Readonly<Record<string, ProjectGroupingMode>>;

/**
 * How this project's repository should be grouped.
 *
 * Keyed on the repository's canonical key rather than on a group key, and that
 * is not a detail. A per-GROUP override would be circular: grouping is what
 * decides which projects form a group, so it cannot first ask the group what it
 * prefers. Keying on the repository is stable regardless of how the answer
 * comes out, and it guarantees every checkout of one repository answers the
 * same — two disagreeing would split a group in half and leave neither half
 * able to find the other.
 *
 * A project with no repository identity has nothing to key an override on, and
 * no subpath to collapse either, so it always takes the default.
 */
export function resolveProjectGroupingMode(
  overrides: ProjectGroupingOverrides,
  project: Pick<Project, "repositoryIdentity">,
): ProjectGroupingMode {
  const canonicalKey = project.repositoryIdentity?.canonicalKey;
  if (!canonicalKey) return DEFAULT_PROJECT_GROUPING_MODE;
  return overrides[canonicalKey] ?? DEFAULT_PROJECT_GROUPING_MODE;
}
